import { z } from 'zod';
import { UserSchema } from '../user.schema.js';
import {
    LoginSchema,
    RegisterSchema,
    ActivateAccountSchema
} from '../auth.schema.js';

export {
    UserSchema,
    LoginSchema,
    RegisterSchema,
    ActivateAccountSchema
};

export const UserRoleEnum = z.enum(['admin', 'user', 'guest', 'auditor']);
export const UserStatusEnum = z.enum(['Active', 'Inactive', 'Pending', 'Suspended']);

export const VerificationChannelSchema = z.enum(['email', 'sms']);

export const PasswordExpirationPeriodSchema = z.enum(['30d', '90d', '180d', '365d']);

export const ForgotPasswordSchema = z.object({
    identity: z.string().min(1).optional(),
    email: z.string().min(1).optional(),
    channel: VerificationChannelSchema,
}).refine((d) => Boolean(d.identity || d.email), {
    message: 'Identity or email is required',
    path: ['identity'],
});

export const ForgotPasswordVerifySchema = z.object({
    identity: z.string().min(1).optional(),
    email: z.string().min(1).optional(),
    channel: VerificationChannelSchema,
    code: z.string().min(4).max(8),
}).refine((d) => Boolean(d.identity || d.email), {
    message: 'Identity or email is required',
    path: ['identity'],
});

export const ResetPasswordSchema = z.object({
    token: z.string().min(1),
    password: z.string().min(6),
    confirmPassword: z.string().min(6),
}).refine((d) => d.password === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
});

export const RefreshTokenSchema = z.object({
    refreshToken: z.string().min(1),
});

export const ChangePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(6),
    confirmNewPassword: z.string().min(6),
});

export const MfaVerifySchema = z.object({
    challengeId: z.string().min(1),
    code: z.string().min(4).max(8),
    channel: VerificationChannelSchema.optional(),
});

export const MfaResendSchema = z.object({
    challengeId: z.string().min(1),
    channel: VerificationChannelSchema.optional(),
});

export const SelfPasswordRequestOtpSchema = z.object({
    channel: VerificationChannelSchema.optional(),
});

export const SelfPasswordConfirmSchema = z.object({
    code: z.string().min(4).max(8).optional(),
    channel: VerificationChannelSchema.optional(),
    currentPassword: z.string().min(1),
    newPassword: z.string().min(6),
    confirmNewPassword: z.string().min(6),
    passwordExpirationPeriod: PasswordExpirationPeriodSchema,
}).refine((d) => d.newPassword === d.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
});

export const PasswordExpiredChangeSchema = z.object({
    challengeId: z.string().min(1),
    currentPassword: z.string().min(1),
    newPassword: z.string().min(6),
    confirmNewPassword: z.string().min(6),
    passwordExpirationPeriod: PasswordExpirationPeriodSchema,
}).refine((d) => d.newPassword === d.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
});

export const SelfMfaRequestOtpSchema = z.object({
    action: z.enum(['enable', 'disable']),
    channel: VerificationChannelSchema.optional(),
});

export const SelfMfaConfirmSchema = z.object({
    action: z.enum(['enable', 'disable']),
    code: z.string().min(4).max(8),
    channel: VerificationChannelSchema.optional(),
});

export const SelfSecurityPatchSchema = z.object({
    mfaChannel: VerificationChannelSchema.optional(),
});

export const LoginResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        user: UserSchema.optional(),
        token: z.string().optional(),
        refreshToken: z.string().optional(),
        mfaRequired: z.boolean().optional(),
        passwordExpired: z.boolean().optional(),
        challengeId: z.string().optional(),
        preferredChannel: VerificationChannelSchema.optional(),
        availableChannels: z.object({
            email: z.boolean(),
            sms: z.boolean(),
        }).optional(),
        maskedDestination: z.string().optional(),
        expiresInSec: z.number().optional(),
    }).optional(),
    message: z.string().optional(),
});

export const AuthUserResponseSchema = z.object({
    success: z.boolean(),
    data: z.object({
        user: UserSchema.optional(),
    }).optional(),
    message: z.string().optional(),
});

export const AuthResponseSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
});
